import { Location } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import dayjs, { Dayjs } from 'dayjs';
import { isEmpty, isNil, uniq } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { EDialogWidth } from '../../core/constants/ui.constants';
import { IDateRangePickerDate } from '../../shared/components/date-range-picker/date-range-picker.interface';
import { IDropdownOption } from '../../shared/components/mat-dropdown/mat-dropdown.component';
import { DateUtilitiesService } from '../../utilities/date-utilities.service';
import { IBaseResponse, IGenericCrudRequestConstructionParameterFilters } from '../../utilities/http-utilities.service';
import { IBoard } from '../settings/boards/boards.interface';
import { EItemType } from '../settings/fields-field-sets/field-sets/field-sets.interface';
import { IItemsFilterCardFields } from './items-filter-card/items-filter-card.interface';
import { ItemsModalComponent } from './items-modal/items-modal.component';
import {
  EUserAssignee,
  IBoardTreeStructure,
  IItemBase,
  IItemJoins,
  TUrlItemScope,
  TUrlResetType,
} from './items.interface';

@Injectable({
  providedIn: 'root',
})
export class ItemsService {
  public readonly refreshItems$: Subject<void> = new Subject<void>();
  public readonly itemsLinked$: Subject<void> = new Subject<void>();
  private isBoardSelected = false;

  constructor(
    private readonly http: HttpClient,
    private readonly location: Location,
    private readonly matDialog: MatDialog,
  ) {}

  /**
   * Kanban and kamishibai each need *some* range to query against even when the filter card has no
   * `dateRange` chip — per business decision, both present that state as "this month" without ever
   * requiring a real chip to exist. List view is deliberately excluded: it requests with no date
   * constraint at all when unspecified, so it never calls this. The fallback must stay local to the
   * caller's own query and never get written back to `filterCardManager`/`filterCardData$` — only an
   * explicit user action (a calendar pick, kamishibai's month-nav arrows) may turn "no date filter"
   * into a real one.
   */
  public getEffectiveDateRange(dateRange: IDateRangePickerDate | null | undefined): {
    endDate: Dayjs;
    startDate: Dayjs;
  } {
    return dateRange?.startDate && dateRange?.endDate
      ? { endDate: dateRange.endDate, startDate: dateRange.startDate }
      : { endDate: dayjs.tz().endOf('month'), startDate: dayjs.tz().startOf('month') };
  }

  public prepareFilters(filterCard: IItemsFilterCardFields): IGenericCrudRequestConstructionParameterFilters[] {
    return [
      ...(!isNil(filterCard.workflowStep) && !isEmpty(filterCard.workflowStep)
        ? [
            {
              field: 'currentWorkflowStepId',
              ids: [...filterCard.workflowStep.map((workflowStep: IDropdownOption) => workflowStep.id)],
            },
          ]
        : []),
      ...(!isNil(filterCard.itemType) && !isEmpty(filterCard.itemType)
        ? [
            {
              field: 'boardItemConfiguration.itemType',
              ids: filterCard.itemType.map((itemType: IDropdownOption<EItemType>) => itemType.id),
            },
          ]
        : []),
    ];
  }

  private prepareFilterForAssignee(assignee: IDropdownOption[] | null): Record<string, unknown>[] {
    const filterConditions = [];

    if (!assignee || assignee.length === 0) {
      return [];
    }

    const assignees = assignee.filter((item) => item.id !== EUserAssignee.unassignedUser);

    if (assignees.length > 0) {
      filterConditions.push({
        assigneeId: {
          $in: assignees.map((assigneeId: IDropdownOption) => assigneeId.id),
        },
      });
    }

    if (assignee.some((item) => item.id === EUserAssignee.unassignedUser)) {
      filterConditions.push({
        assigneeId: {
          $isnull: true,
        },
      });
    }

    return filterConditions;
  }

  private buildDateRangeOrCondition(startDate: Dayjs | null, endDate: Dayjs | null): Record<string, unknown> | null {
    if (!startDate || !endDate) {
      return null;
    }

    const start: string | undefined = !isNil(startDate)
      ? DateUtilitiesService.convertUserDateToUTCString(startDate)
      : undefined;
    const end: string | undefined = !isNil(endDate)
      ? DateUtilitiesService.convertUserDateToUTCString(endDate)
      : undefined;

    return {
      $or: [
        {
          $and: [{ createdAt: { $gte: start } }, { createdAt: { $lte: end } }],
        },
        {
          $and: [{ dueDate: { $gte: start } }, { dueDate: { $lte: end } }],
        },
        {
          $and: [{ eventDate: { $gte: start } }, { eventDate: { $lte: end } }],
        },
      ],
    };
  }

  public prepareAdditionalCustomSearch(
    filterCard: IItemsFilterCardFields,
    boardTreeStructure: IBoardTreeStructure[],
  ): Record<string, unknown>[] {
    const filterAndConditions = [];

    if (filterCard.dateRange) {
      const dateRangeCondition = this.buildDateRangeOrCondition(
        filterCard.dateRange.startDate,
        filterCard.dateRange.endDate,
      );

      if (dateRangeCondition) {
        filterAndConditions.push(dateRangeCondition);
      }
    }

    if (!isNil(filterCard.board) && !isEmpty(filterCard.board)) {
      const boardIds = [...this.getBoardIds(filterCard, boardTreeStructure)];

      filterAndConditions.push({
        $or: [
          {
            escalatedBoardId: { $in: boardIds },
          },
          {
            boardId: { $in: boardIds },
          },
        ],
      });
    }

    if (filterCard.itemScope === 'escalated') {
      filterAndConditions.push({
        escalatedBoardId: { $notnull: true },
      });
    }

    const assigneeConditions: Record<string, unknown>[] = this.prepareFilterForAssignee(filterCard.assignee);

    if (assigneeConditions.length > 0) {
      filterAndConditions.push({
        $or: assigneeConditions,
      });
    }

    if (!isNil(filterCard.reporter) && !isEmpty(filterCard.reporter)) {
      filterAndConditions.push({
        reporterId: { $in: filterCard.reporter.map((reporter: IDropdownOption) => reporter.id) },
      });
    }

    const itemCategoryConditions: Record<string, unknown>[] = this.prepareFilterForItemCategory(
      filterCard.itemCategory,
    );

    if (itemCategoryConditions.length > 0) {
      filterAndConditions.push({
        $or: itemCategoryConditions,
      });
    }

    return filterAndConditions.length > 0 ? [{ $and: filterAndConditions }] : [];
  }

  public getTreeStructure(): Observable<IBaseResponse<IBoardTreeStructure[]>> {
    return this.http.get<IBaseResponse<IBoardTreeStructure[]>>('boards/tree-structure');
  }

  public getBoardName(boardKey: string): Observable<IBaseResponse<IBoard[]>> {
    const params: HttpParams = new HttpParams()
      .set('limit', 1)
      .set('fields', 'name,key')
      .set('s', JSON.stringify({ key: { $endsL: boardKey, $startsL: boardKey } }));

    return this.http.get<IBaseResponse<IBoard[]>>('boards', { params });
  }

  public getItem(
    itemKey?: string,
    itemId?: number,
  ): Observable<IBaseResponse<(IItemBase & Pick<IItemJoins, 'board'>)[]>> {
    const params: HttpParams = new HttpParams()
      .set('limit', 1)
      .set(
        'fields',
        'name,description,boardId,boardItemConfigurationId,fieldSetId,currentWorkflowStepId,values,assigneeId,key,dueDate,createdBy,createdAt,',
      )
      .append('join', 'board||name,key')
      .append('join', 'boardItemConfiguration||id,itemType')
      .append('join', 'boardItemConfiguration.itemCategory||name')
      .append('join', 'boardItemConfiguration.workflow||id')
      .append('join', 'boardItemConfiguration.workflow.workflowStepAssignments||id')
      .append('join', 'boardItemConfiguration.workflow.workflowStepAssignments.nextWorkflowStep||name,isDefault')
      .append('join', 'boardItemConfiguration.workflow.workflowStepAssignments.workflowStep||name,isDefault')
      .append('join', 'assignee||name')
      .append('join', 'reporter||name')
      .append('join', 'currentWorkflowStep||name')
      .set(
        's',
        JSON.stringify({
          ...(itemKey ? { key: { $endsL: itemKey, $startsL: itemKey } } : {}),
          ...(itemId ? { id: { $eq: itemId } } : {}),
        }),
      );

    return this.http.get<IBaseResponse<(IItemBase & Pick<IItemJoins, 'board'>)[]>>('items', { params });
  }

  public triggerRefresh(): void {
    this.refreshItems$.next();
  }

  public triggerItemsLinked(): void {
    this.itemsLinked$.next();
  }

  public openItemEditDialogAndSetUrl(
    boardKey: string | undefined,
    itemId: number | undefined,
    itemKey: string | undefined,
    submitButtonText: string,
  ): void {
    this.setSelectedItemUrl(boardKey, itemKey);

    if (!itemId) {
      return;
    }

    const dialogRef: MatDialogRef<ItemsModalComponent> = this.matDialog.open(ItemsModalComponent, {
      autoFocus: false,
      data: {
        idForEdit: itemId,
        submitButtonText,
        title: itemKey,
      },
      disableClose: true,
      maxWidth: '100%',
      restoreFocus: false,
      width: EDialogWidth.large,
    });

    dialogRef.afterClosed().subscribe(() => {
      this.setSelectedItemUrl(boardKey, itemKey, true);
    });
  }

  private setSelectedItemUrl(boardKey: string | undefined, itemKey: string | undefined, isReset = false): void {
    if (!isReset) {
      this.isBoardSelected = !!this.location.path().split('/')[2];
    }

    const scope: TUrlItemScope = this.location.path().split('/')[3] as TUrlItemScope;
    const resetType: TUrlResetType | undefined = isReset ? (this.isBoardSelected ? 'itemKey' : 'full') : undefined;

    if (resetType === 'full') {
      return this.location.go('items');
    }

    if (resetType === 'itemKey') {
      return this.location.go(`items/${boardKey}/${scope ?? 'all'}`);
    }

    return this.location.go(`items/${boardKey}/${scope ?? 'all'}/${itemKey}`);
  }

  private getBoardIds(filterCard: IItemsFilterCardFields, boardTreeStructure: IBoardTreeStructure[]): number[] {
    if (filterCard.itemScope === 'self' || filterCard.itemScope === 'escalated') {
      return filterCard.board?.map((board: IDropdownOption) => board.id) ?? [];
    }

    const selectedBoardIds = filterCard.board?.map((board: IDropdownOption) => board.id) ?? [];
    let childBoardIds: number[] = [];

    for (const boardId of selectedBoardIds) {
      if (childBoardIds.includes(boardId)) {
        continue;
      }

      childBoardIds = [...childBoardIds, ...this.getChildBoardIds(boardId, boardTreeStructure)];
    }

    return uniq([...selectedBoardIds, ...childBoardIds]);
  }

  private getChildBoardIds(boardId: number, boardTree: IBoardTreeStructure[]): number[] {
    const ids: number[] = [];

    for (const board of boardTree) {
      if (board.id === boardId && board.children) {
        ids.push(...board.children.map((child) => child.id));

        for (const child of board.children) {
          ids.push(...this.getChildBoardIds(child.id, boardTree));
        }
      }

      if (board.children.length) {
        ids.push(...this.getChildBoardIds(boardId, board.children));
      }
    }

    return ids;
  }

  private prepareFilterForItemCategory(itemCategory: IDropdownOption[] | null): Record<string, unknown>[] {
    if (!itemCategory || itemCategory.length === 0) {
      return [];
    }

    const filterConditions: Record<string, unknown>[] = [];

    const categoryIds: number[] = itemCategory
      .filter((item) => !isNil(item.id) && item.id !== 0)
      .map((item) => item.id);

    if (categoryIds.length > 0) {
      filterConditions.push({
        'boardItemConfiguration.itemCategoryId': {
          $in: categoryIds,
        },
      });
    }

    if (itemCategory.some((item) => isNil(item.id) || item.id === 0)) {
      filterConditions.push({
        'boardItemConfiguration.itemCategoryId': {
          $isnull: true,
        },
      });
    }

    return filterConditions;
  }
}
